import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';

import { productValidator } from '@eduzz-product-manager/validators';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { Input } from '@/components/form/Input';
import { FormBlock } from '@/components/FormBlock';
import { useFormGroupListener } from '@/hooks/useFormGroupListener';
import { useZustandSync } from '@/hooks/useZustandSync';
import useAIProductExampleStore from '@/stores/AIProductExampleStore';
import useProductStore, { productStore } from '@/stores/productStore';

import { type Event } from '../FormGroup';
import { NiceActions } from '../NiceActions';

const productTitleSchema = z.object({
  title: productValidator.shape.title
});

type ProductTitleFormValues = z.infer<typeof productTitleSchema>;

const FIELD_NAME = 'title';
const PRODUCT_TITLE_MAX_LENGTH = 120;

export default function ProductTitle() {
  const store = productStore.getState();

  const form = useForm<ProductTitleFormValues>({
    resolver: zodResolver(productTitleSchema),
    mode: 'onChange',
    defaultValues: { [FIELD_NAME]: useProductStore(d => d[FIELD_NAME]) }
  });

  useZustandSync<ProductTitleFormValues>(form.control, data => ({
    [FIELD_NAME]: data[FIELD_NAME]
  }));

  const initialValue = useRef(form.control._defaultValues[FIELD_NAME]);
  const productTitle = useProductStore(d => d[FIELD_NAME]);
  const aiProductExampleStore = useAIProductExampleStore(d => d);
  const showIaButton = aiProductExampleStore.hasData();

  useFormGroupListener(FIELD_NAME, async (event: Event) => {
    if (event.type === 'validate') {
      return true;
    }

    if (event.type === 'error' && event.payload?.name === FIELD_NAME) {
      form.setError(FIELD_NAME, { message: event.payload.value });
      return true;
    }
  });

  useEffect(() => {
    if (productTitle === initialValue.current) {
      return;
    }

    store.setIaField('product-title', false);
  }, [productTitle, store]);

  return (
    <FormBlock
      name='product-title'
      lengthIndicator={`${productTitle.length}/${PRODUCT_TITLE_MAX_LENGTH}`}
      form={form}
      title='Título do Produto'
      preview={<>{productTitle}</>}
    >
      <Input
        id='product-title'
        maxLength={PRODUCT_TITLE_MAX_LENGTH}
        placeholder='Título do seu produto'
        name={FIELD_NAME}
      />
      {showIaButton && (
        <NiceActions
          onAIGenerate={() => {
            const example = aiProductExampleStore.getExampleOf('title');

            if (example) {
              initialValue.current = example;
              form.setValue(FIELD_NAME, example);
              form.clearErrors(FIELD_NAME);
              store.setIaField('product-title', true);
            }
          }}
        />
      )}
    </FormBlock>
  );
}
